// src/api/diffsona.ts
import { PromptPayload } from '../components/CreatePage';
import { API_ENDPOINT } from '../constants';

export async function getDiffsona(slug: string) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${slug}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load diffsona');
  return res.json();
}

export async function retrainDiffsona(slug: string) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${slug}/retrain`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to retrain diffsona');
}

export async function addMedia(slug: string, mediaId: string, primary = true) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${slug}/add-media`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ media_id: mediaId, primary }),
  });
  if (!res.ok) throw new Error('Failed to add media');
  return res.json();
}

export async function removeMedia(slug: string, mediaId: string, primary = true) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${slug}/remove-media`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ media_id: mediaId, primary }),
  });
  if (!res.ok) throw new Error('Failed to remove media');
  return res.json();
}

export async function uploadMedia(file: File): Promise<{ media_id: string }> {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_ENDPOINT}/upload-media`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });

  if (!res.ok) throw new Error('Upload failed');
  return res.json();
}

export type GeneratedPage = {
  diffsona_id: number;
  image_urls: string[];     // relative paths
  next_cursor?: string | null; // opaque cursor "RFC3339|jobID"
};

export async function getGeneratedContent(
  slug: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<GeneratedPage> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);

  const qs = params.toString();
  const url = `${API_ENDPOINT}/diffsona/${encodeURIComponent(slug)}/generated-content${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch generated content');
  return res.json();
}

export type CreateDiffsonaFromDescriptionRequest = {
  name: string;
  base: string;
  age: string;
  ethnicity: string;
  hair: string;
  face: string;
  body: string;
};

export type CreateDiffsonaFromDescriptionResponse = {
  id: number;
  slug: string;
};

export async function createDiffsonaFromDescription(
  body: CreateDiffsonaFromDescriptionRequest
): Promise<CreateDiffsonaFromDescriptionResponse> {
  const res = await fetch(`${API_ENDPOINT}/create-diffsona-from-description`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Failed to create diffsona');
  }
  return res.json();
}

export type FeedPost = {
  id: number;
  image_key: string;
  caption: string;
  meta?: any;
  user_id: string;
  created_at: string;
  tip_total?: number;
};

export type FeedResponse = {
  items: FeedPost[];
  next_cursor?: number;
};

export async function shareToFeed(input: {
  image_key?: string;        // preferred
  image_url?: string;        // fallback if key unknown
  caption?: string;
  meta?: any;
}): Promise<FeedPost> {
  const res = await fetch(`${API_ENDPOINT}/feed/share`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to share'));
  return res.json();
}

export async function removeFeedPost(id: number): Promise<void> {
  const res = await fetch(`${API_ENDPOINT}/feed/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to remove'));
}

export async function getFeed(limit = 24, cursor?: number): Promise<FeedResponse> {
  const u = new URL(`${API_ENDPOINT}/feed`);
  u.searchParams.set('limit', String(limit));
  if (cursor) u.searchParams.set('cursor', String(cursor));
  const res = await fetch(u.toString(), { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to load feed'));
  return res.json();
}

// src/api.ts
export async function getCurrentDiffsona(slug: string) {
  const u = new URL(`${API_ENDPOINT}/diffsona/${slug}/current`);
  const res = await fetch(u.toString(), { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to load current diffsona'));
  return res.json() as Promise<{
    name: string;
    slug: string;
    id: number;
    images: { id: number; image_url: string; image_key: string; created_at: string }[];
  }>;
}

// src/client/ApiClient.ts
export async function getDiffsonaPrompts(slug: string) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${encodeURIComponent(slug)}/prompts`, { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to load prompts'));
  return res.json() as Promise<{
    name: string;
    base_prompt: string; age_prompt: string; ethnicity_prompt: string;
    hair_prompt: string; face_prompt: string; body_prompt: string;
    bust_prompt?: string; waist_prompt?: string; hips_prompt?: string;
  }>;
}

export async function updateDiffsonaPrompts(slug: string, body: {
  name?: string;
  base?: string; age?: string; ethnicity?: string; hair?: string; face?: string; body?: string;
  bust?: string; waist?: string; hips?: string;
}) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${encodeURIComponent(slug)}/prompts`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to update prompts'));
  return res.json();
}

export async function getCreditsBalance() {
  const r = await fetch(`${API_ENDPOINT}/credits/balance`, { credentials: 'include' });
  if (!r.ok) throw new Error('balance');
  return r.json() as Promise<{ balance: number }>;
}

export async function estimateCredits(prompt: PromptPayload) {
  const r = await fetch(`${API_ENDPOINT}/credits/estimate`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  if (!r.ok) throw new Error('estimate');
  return r.json() as Promise<{ cost: number; balance: number; can_afford: boolean }>;
}

export async function devBuyCredits(amount: number) {
  const r = await fetch(`${API_ENDPOINT}/credits/purchase/dev`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount }),
  });
  if (!r.ok) throw new Error('purchase');
  return r.json() as Promise<{ balance: number }>;
}

// src/client/ApiClient.ts
export type MyDiffsonaItem = {
  slug: string;
  name: string;
  created_at: string;
  images: string[]; // NEW
};

export async function getMyDiffsonas(limit = 12, images = 6): Promise<MyDiffsonaItem[]> {
  const u = new URL(`${API_ENDPOINT}/api/my/diffsonas`);
  u.searchParams.set('limit', String(limit));
  u.searchParams.set('images', String(images));
  const res = await fetch(u.toString(), { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to load diffsonas'));
  return res.json();
}


// src/client/ApiClient.ts
export async function getDiffsonaForEdit(slug: string) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${encodeURIComponent(slug)}/edit`, {
    credentials: 'include'
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to load'));
  return res.json() as Promise<{
    id: number; slug: string; name: string;
    base_prompt: string; age_prompt: string; ethnicity_prompt: string;
    hair_prompt: string; face_prompt: string; body_prompt: string;
  }>;
}

export async function updateDiffsonaEdit(
  slug: string,
  body: { name?: string; base?: string; age?: string; ethnicity?: string; hair?: string; face?: string; body?: string }
) {
  const res = await fetch(`${API_ENDPOINT}/diffsona/${encodeURIComponent(slug)}/edit`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to save'));
  return res.json();
}

export async function createCheckoutSession(pack: number, returnTo = ""): Promise<{ id: string; pub_key: string }> {
  const r = await fetch(`${API_ENDPOINT}/credits/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pack, return_to: returnTo }),
  });
  if (!r.ok) throw new Error(await r.text().catch(() => 'checkout failed'));
  return r.json();
}

export type MyGeneratedPage = {
  image_urls: string[];
  next_cursor?: string | null;
};

export async function getMyGenerated(
  limit = 24,
  cursor?: string
): Promise<MyGeneratedPage> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const url = `${API_ENDPOINT}/me/generated-content?${params.toString()}`;

  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load your generated images');
  return res.json();
}

export type TipResponse = { ok: boolean; tip_total: number };

export async function tipFeedPost(postId: number, amount: number): Promise<TipResponse> {
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/tip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ amount }),
  });
  if (res.status === 402) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'insufficient_credits');
  }
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to tip'));
  return res.json();
}

export type ReactionsResponse = { counts: Record<string, number>; mine: Record<string, boolean> };
export async function reactToPost(postId: number, emoji: string): Promise<ReactionsResponse> {
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/react`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error(await res.text().catch(()=>'Failed to react'));
  return res.json();
}
export async function unreactToPost(postId: number, emoji: string): Promise<ReactionsResponse> {
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/react`, {
    method: 'DELETE', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji }),
  });
  if (!res.ok) throw new Error(await res.text().catch(()=>'Failed to unreact'));
  return res.json();
}
export async function getReactions(postId: number): Promise<ReactionsResponse> {
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/reactions`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load reactions');
  return res.json();
}

export type FeedComment = { id: number; user_id: string; text: string; created_at: string };
export type CommentsPage = { items: FeedComment[]; next_cursor?: string | null };

export async function createComment(postId: number, text: string): Promise<FeedComment> {
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/comments`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to comment'));
  return res.json();
}

export async function getComments(postId: number, limit=20, cursor?: string): Promise<CommentsPage> {
  const q = new URLSearchParams({ limit: String(limit) });
  if (cursor) q.set('cursor', cursor);
  const res = await fetch(`${API_ENDPOINT}/feed/${postId}/comments?${q}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load comments');
  return res.json();
}

export async function deleteComment(commentId: number): Promise<void> {
  const res = await fetch(`${API_ENDPOINT}/feed/comments/${commentId}`, {
    method: 'DELETE', credentials: 'include'
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to delete'));
}
